import type { MRT_ColumnFiltersState } from "material-react-table";
import { euroToEurocent } from "@/helper/formattingValues";
import {
  buildDateRangeApiParams,
  buildExactDateApiParams,
  isAnyDateRangeFilterValue,
  isDateRangeFilterEnabled,
  isDateRangeValueEmpty,
  normalizeDateRangeValue,
  resolveDateFilterFieldPath,
  resolveFilterDateRangeFormat,
} from "@/components/dateTime/genericDateFilterUtils";
import type { CrudColumnDef } from "@/components/tables/crudColumnDef";
import {
  getDurationFilterApiParams,
  isDurationFilterRangeValue,
} from "@/components/tables/genericCrudTableDurationFilterUtils";
import type { EntityAdapter } from "@/components/tables/entityAdapters/entityAdapter";

export type ApiParams = Record<string, unknown>;

export interface NormalizedColumnFilter {
  column: string;
  value: any;
  customFilterId: string;
  filterVariant: string;
  customParam: string;
  useGenericParam: boolean;
}

// mirrors material-react-table getColumnId (prepareColumns): id ?? accessorKey ?? header
export const getMrtColumnId = (column: CrudColumnDef): string | undefined =>
  column.id ??
  (column.accessorKey !== undefined ? String(column.accessorKey) : undefined) ??
  (typeof column.header === "string" ? column.header : undefined);

const findColumnByMrtId = (columns: CrudColumnDef[], filterId: string) =>
  columns.find((column) => getMrtColumnId(column) === filterId);

const findColumnByExplicitId = (columns: CrudColumnDef[], filterId: string) =>
  columns.find((column) => column.id === filterId); // preserved quirk: date/duration options are only found on columns with an explicit id

const buildQueryKey = (
  baseKey: string,
  customParam: string,
  useGenericParam: boolean
) =>
  customParam
    ? `${baseKey}${customParam}`
    : useGenericParam
    ? `${baseKey}__icontains`
    : baseKey;

export const buildColumnFilterApiParams = (
  columnFilters: MRT_ColumnFiltersState,
  columns: CrudColumnDef[],
  entityAdapter?: EntityAdapter
): ApiParams => {
  const params: ApiParams = {};
  if (columnFilters.length === 0) {
    return params;
  }

  const shouldConvertPriceToEurocent =
    entityAdapter?.convertsPriceToEurocent ?? true;
  const allowsDateRangeFilters = entityAdapter?.allowsDateRangeFilters ?? true;

  const normalizedFilters: NormalizedColumnFilter[] = columnFilters.map((filter) => {
    const columnDef: CrudColumnDef =
      findColumnByMrtId(columns, filter.id) ?? ({} as CrudColumnDef);

    return {
      column: filter.id,
      value: filter.value as any,
      customFilterId: columnDef.customColumnFilterId || "",
      filterVariant: columnDef.filterVariant || "",
      customParam: columnDef.customFilterParameter || "",
      useGenericParam: columnDef.setNoFilterParameter !== true,
    };
  });

  if (entityAdapter?.columnFiltersParams) {
    Object.assign(params, entityAdapter.columnFiltersParams(normalizedFilters));
  }

  normalizedFilters.forEach((filter) => {
    const {
      column,
      customFilterId,
      filterVariant,
      customParam,
      useGenericParam,
    } = filter;
    let value = filter.value;

    if (filterVariant === "date") {
      const originalColumnDef = findColumnByExplicitId(columns, column);

      if (
        originalColumnDef &&
        isDateRangeFilterEnabled(originalColumnDef, allowsDateRangeFilters) &&
        isAnyDateRangeFilterValue(value)
      ) {
        const range = normalizeDateRangeValue(value);
        if (isDateRangeValueEmpty(range)) {
          return;
        }
        const fieldPath = resolveDateFilterFieldPath(originalColumnDef, column);
        Object.assign(
          params,
          buildDateRangeApiParams(
            fieldPath,
            range,
            resolveFilterDateRangeFormat(originalColumnDef),
            originalColumnDef.dateFilterApiParams
          )
        );
        return;
      }

      Object.assign(
        params,
        buildExactDateApiParams(
          resolveDateFilterFieldPath(
            originalColumnDef ?? { id: column },
            column
          ),
          value,
          {
            customFilterId,
            columnId: column,
            customParam,
            useGenericParam,
            dateFilterApiParams: originalColumnDef?.dateFilterApiParams,
            mapExactDate: entityAdapter?.mapExactDateFilter,
          }
        )
      );
      return;
    }

    if (filterVariant === "percent") {
      const num = Number(String(value).replace(",", "."));
      if (!Number.isNaN(num)) {
        value = String(num / 100);
      }
    }

    if (filterVariant === "duration") {
      const originalColumnDef = findColumnByExplicitId(columns, column);
      const baseKey = customFilterId || column;
      const queryKey = buildQueryKey(baseKey, customParam, useGenericParam);
      const durationFilterKey = isDurationFilterRangeValue(value)
        ? baseKey
        : queryKey;
      Object.assign(
        params,
        getDurationFilterApiParams(durationFilterKey, value, {
          apiUnit: originalColumnDef?.durationFilterApiUnit,
          apiValueTransformer: originalColumnDef?.durationFilterApiValue,
          rangeParameterSuffixes:
            originalColumnDef?.durationFilterRangeParameterSuffixes,
        })
      );
      return;
    }

    if (filterVariant === "checkbox") {
      params[customFilterId || column] = value;
      return;
    }

    if (filterVariant === "price") {
      const baseKey = customFilterId || column;
      const queryKey = buildQueryKey(baseKey, customParam, useGenericParam);

      if (entityAdapter?.mapPriceFilter) {
        Object.assign(params, entityAdapter.mapPriceFilter(filter, queryKey));
        return;
      }

      const normalized = value.toString().replace(",", ".");
      params[queryKey] = shouldConvertPriceToEurocent
        ? euroToEurocent(normalized)
        : Number(normalized);
      return;
    }

    const adapterFilterParams = entityAdapter?.mapColumnFilter?.({
      ...filter,
      value, // percent filters arrive here already converted
    });
    if (adapterFilterParams) {
      Object.assign(params, adapterFilterParams);
      return;
    }

    if (customFilterId) {
      if (customParam) {
        params[customFilterId + customParam] = value;
      } else if (!useGenericParam) {
        params[customFilterId] = value;
      } else {
        params[customFilterId + "__icontains"] = value;
      }
      return;
    }

    if (customParam) {
      params[column + customParam] = value;
    } else if (!useGenericParam) {
      params[column] = value;
    } else {
      params[column + "__icontains"] = value;
    }

    entityAdapter?.afterColumnFilter?.({ ...filter, value }, params);
  });

  return params;
};

export interface ListRequestContext {
  columns: CrudColumnDef[];
  columnFilters: MRT_ColumnFiltersState;
  globalFilter: string;
  ordering: string;
  pagination: { pageIndex: number; pageSize: number };
  searchId?: string;
  companySubjectId?: string;
  additionalUrlParameter?: Record<string, unknown>;
  additionalRoutePartsForGet?: Record<string, unknown>;
  additionalKeyForParameteresToRender?: string;
  expandedDepth?: boolean;
  entityAdapter?: EntityAdapter;
}

export interface ListRequestParams {
  additionalUrlParameters: ApiParams;
  additionalRouteParts: Record<string, unknown>;
  parametersToRender: Record<string, unknown>;
}

export const buildListRequestParams = (
  ctx: ListRequestContext
): ListRequestParams => {
  const additionalUrlParameters: ApiParams = {};

  if (ctx.companySubjectId) {
    additionalUrlParameters["impersonate_subject"] = ctx.companySubjectId;
  }
  if (ctx.searchId && ctx.searchId !== "" && ctx.entityAdapter?.searchIdParameter) {
    additionalUrlParameters[ctx.entityAdapter.searchIdParameter] = ctx.searchId;
  } else if (ctx.searchId && ctx.searchId !== "") {
    additionalUrlParameters["subject"] = ctx.searchId;
  }
  ctx.entityAdapter?.adjustListParameters?.(additionalUrlParameters, ctx);
  if (ctx.additionalUrlParameter) {
    for (const key in ctx.additionalUrlParameter) {
      additionalUrlParameters[key] = ctx.additionalUrlParameter[key];
    }
  }

  Object.assign(
    additionalUrlParameters,
    buildColumnFilterApiParams(
      ctx.columnFilters,
      ctx.columns,
      ctx.entityAdapter
    )
  );

  const additionalRouteParts = ctx.additionalRoutePartsForGet || {};

  const trimmedGlobalFilter =
    typeof ctx.globalFilter === "string"
      ? ctx.globalFilter.trim()
      : ctx.globalFilter;

  const parametersToRender: Record<string, unknown> = {
    depth: ctx.expandedDepth ? "1" : "0",
    ordering: ctx.ordering,
    limit: ctx.pagination.pageSize,
    offset: ctx.pagination.pageIndex * ctx.pagination.pageSize,
    additionalUrlParameters,
  };
  if (trimmedGlobalFilter) {
    if (ctx.additionalKeyForParameteresToRender) {
      parametersToRender[ctx.additionalKeyForParameteresToRender] =
        trimmedGlobalFilter;
    } else {
      parametersToRender.search = trimmedGlobalFilter;
    }
  }

  return { additionalUrlParameters, additionalRouteParts, parametersToRender };
};
